import type { FieldValidator, GameService } from './types'

// i18n keys, not literal text — resolve with $t() at render time. Shared by the
// form field options below and by SegmentsSettingsBar's in-game summary.
export const SEGMENT_MODES = ['match_settings.segment_mode_race', 'match_settings.segment_mode_points']
export const SEGMENT_ORDER = [
    'match_settings.target_order_low_to_high',
    'match_settings.target_order_high_to_low',
    'match_settings.target_order_random',
]
export const POINTS_MODES = ['match_settings.points_mode_ceiling', 'match_settings.points_mode_rounds']

const RACE_MODE = 0
const POINTS_MODE = 1

interface SegmentTarget {
    number: number
    type: string
}

// The dart number scored at each bull ring; a bull target's `number` lines up
// with the dart that hits it (matches the backend / SegmentsSelect).
const BULL_TARGET_NUMBERS: Record<string, number> = {
    B: 25, // single bull
    'B.E': 50, // bullseye
}

const isBullTarget = (segment: SegmentTarget): boolean =>
    segment?.type === 'B' || segment?.type === 'B.E'

const FULL_RING = 20

// True when the selection is the full Triple ring (all 20 numbers, every one 'T').
// Mirrors SegmentsSelect.ringTabForTargets: a triples drill has no bull target, so
// the bull control is hidden and stale bulls are not folded in at submit.
const isRing = (settings: { segments_to_hit?: unknown }, type: string): boolean => {
    const segments = Array.isArray(settings.segments_to_hit)
        ? (settings.segments_to_hit as SegmentTarget[])
        : []
    return segments.length === FULL_RING && segments.every((segment) => segment?.type === type)
}

/**
 * Race mode needs at least one target to play. Segments are picked in
 * `segments_to_hit` and the single bull / bullseye in `bull_targets` (folded
 * together only at submit), so a game is valid as long as either is non-empty —
 * letting the player race just the bull, just the bullseye, or just segments.
 */
const segmentsTargetValidator: FieldValidator = (settings) => {
    const segments = Array.isArray(settings.segments_to_hit) ? settings.segments_to_hit : []
    const bulls = Array.isArray(settings.bull_targets) ? settings.bull_targets : []

    if (segments.length === 0 && bulls.length === 0) {
        return 'match_settings.segments_target_required'
    }

    return null
}

const game: GameService = {
    name: 'segments',
    category: 'minigames',
    placeholder: false,
    disable: true,
    onlineAvailable: false,
    canPause: true,
    form: [
        {
            titleKey: 'match_settings.section_select_mode',
            fields: [
                {
                    component: 'select',
                    key: 'segment_mode',
                    divider: true,
                    options: [
                        { value: 0, labelKey: SEGMENT_MODES[0] },
                        { value: 1, labelKey: SEGMENT_MODES[1] },
                    ],
                },
                {
                    component: 'segments',
                    key: 'segments_to_hit',
                    visibleWhen: (settings) => settings.segment_mode === 0,
                    validate: segmentsTargetValidator,
                },
                {
                    component: 'select',
                    key: 'hits_per_target',
                    labelKey: 'match_settings.hits_per_target',
                    visibleWhen: (settings) => settings.segment_mode === 0,
                    options: [
                        { value: 1, label: '1' },
                        { value: 2, label: '2' },
                        { value: 3, label: '3' },
                    ],
                },
                {
                    component: 'select',
                    key: 'points_mode',
                    divider: true,
                    visibleWhen: (settings) => settings.segment_mode === 1,
                    options: [
                        { value: 0, labelKey: POINTS_MODES[0] },
                        { value: 1, labelKey: POINTS_MODES[1] },
                    ],
                },
                {
                    component: 'dropdown',
                    key: 'points_ceiling',
                    labelKey: 'match_settings.points_ceiling',
                    visibleWhen: (settings) => settings.points_mode === 0 && settings.segment_mode === 1,
                    options: [20, 50, 100, 150, 200].map((value) => ({
                        value,
                        label: String(value),
                    })),
                },
                {
                    component: 'incrementer',
                    key: 'rounds',
                    labelKey: 'match_settings.rounds',
                    min: 1,
                    max: 3,
                    visibleWhen: (settings) => settings.segment_mode === 1 && settings.points_mode === 1,
                },
            ],
        },
        {
            titleKey: 'match_settings.section_target_order',
            fields: [
                {
                    component: 'select',
                    key: 'target_order',
                    divider: true,
                    options: [
                        { value: 0, labelKey: SEGMENT_ORDER[0] },
                        { value: 1, labelKey: SEGMENT_ORDER[1] },
                        { value: 2, labelKey: SEGMENT_ORDER[2] },
                    ],
                },
                {
                    component: 'toggle',
                    key: 'include_bulls',
                    labelKey: 'match_settings.include_bulls',
                    // Pure-UI key folded into segments_to_hit at submit, so its
                    // default lives here, not in the backend default_settings.
                    default: true,
                    visibleWhen: (settings) => settings.segment_mode === 1,
                },
                {
                    component: 'checkboxes',
                    key: 'bull_targets',
                    labelKey: 'match_settings.bull_targets',
                    visibleWhen: (settings) => settings.segment_mode === RACE_MODE && !isRing(settings, 'T'),
                    options: [
                        { value: 'B', labelKey: 'match_settings.single_bull' },
                        { value: 'B.E', labelKey: 'match_settings.bullseye' },
                    ],
                },
            ],
        },
        // "Include in stats" is rendered once, universally, by MatchSetupView's
        // static footer toggle — not per-game, so it isn't listed here.
    ],
    /**
     * Fold the two "include bulls" inputs into `segments_to_hit` so the backend
     * never has to know about them — they are pure UI conveniences. Bulls are
     * stripped first, then re-derived from the current settings, so this is safe
     * to run more than once (e.g. a save retry) and produces no duplicates.
     */
    beforeSubmit(settings) {
        const existing = Array.isArray(settings.segments_to_hit)
            ? (settings.segments_to_hit as SegmentTarget[])
            : []
        const targets = existing.filter((segment) => !isBullTarget(segment))

        const bulls: SegmentTarget[] = []
        if (
            settings.segment_mode === RACE_MODE &&
            !isRing(settings, 'T') &&
            Array.isArray(settings.bull_targets)
        ) {
            ;(settings.bull_targets as string[]).forEach((type) => {
                if (type in BULL_TARGET_NUMBERS) {
                    bulls.push({ type, number: BULL_TARGET_NUMBERS[type] })
                }
            })
        }
        // Points mode plays the bull as one target, like a number: single bull
        // scores 1 and bullseye 2 on it.
        if (settings.segment_mode === POINTS_MODE && settings.include_bulls) {
            bulls.push({ type: 'B', number: 25 })
        }

        return { ...settings, segments_to_hit: [...targets, ...bulls] }
    },
    content: {
        type: 'Segments',
        duration: [15, 30],
        difficulty: 'all',
        player_count: '1 - 4',
        images: {
            banner: '',
            tile: '',
        },
    },
}

export default game
